import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import httpService from "../services/httpService";
import { useAuthStore } from "../store/store";
import Cropper from 'react-easy-crop';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Modal from 'react-bootstrap/Modal';

import {
    FaCamera, FaUser, FaLock, FaMicrophone,
    FaBell, FaSave,
} from "react-icons/fa";

import "../styles/dashbaords.scss";
import "../styles/Profile.scss";
import PageHero from "../components/PageHero";
import UserAvatar from "../components/UserAvatar";

const Profile = () => {
    const navigate    = useNavigate();
    const setAuthUser = useAuthStore(state => state.setUser);

    const [loading, setLoading]                   = useState(true);
    const [error, setError]                       = useState(null);
    const [success, setSuccess]                   = useState(null);
    const [user, setUser]                         = useState(null);
    const [listener, setListener]                 = useState(null);
    const [podcaster, setPodcaster]               = useState(null);
    const [saving, setSaving]                     = useState(false);
    const [uploadingAvatar, setUploadingAvatar]   = useState(false);
    const [switchingRole, setSwitchingRole]       = useState(false);
    const [showSwitchModal, setShowSwitchModal]   = useState(false);

    // Crop states
    const [showCropModal, setShowCropModal]           = useState(false);
    const [imageSrc, setImageSrc]                     = useState(null);
    const [crop, setCrop]                             = useState({ x: 0, y: 0 });
    const [zoom, setZoom]                             = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels]   = useState(null);
    const [rotation, setRotation]                     = useState(0);

    // Form states
    const [formData, setFormData] = useState({
        first_name: '', last_name: '', email: '',
        phone: '', country: '', town: '',
    });

    const [passwordData, setPasswordData] = useState({
        current_password: '', new_password: '', new_password_confirmation: '',
    });

    const [podcasterData, setPodcasterData] = useState({
        contact_info: '', subscription_type: '',
    });

    const NOTIF_TYPES = [
        { key: 'comment_reply',  label: 'Comment Reply' },
        { key: 'user_mentioned', label: 'User Mentioned' },
        { key: 'new_discussion', label: 'New Discussion' },
    ];
    const DEFAULT_PREF = { in_app: true, email: true, sms: false };

    const [notifPrefs, setNotifPrefs] = useState(
        NOTIF_TYPES.reduce((acc, { key }) => ({ ...acc, [key]: { ...DEFAULT_PREF } }), {})
    );
    const [savingPref, setSavingPref] = useState({});

    /* ── Data loading ── */
    useEffect(() => { loadProfile(); loadNotifPrefs(); }, []);

    const loadProfile = () => {
        setLoading(true);
        httpService.get("/dash/profile")
            .then((res) => {
                setUser(res.data.user);
                setListener(res.data.listener);
                setPodcaster(res.data.podcaster);
                setFormData({
                    first_name: res.data.user.first_name || '',
                    last_name:  res.data.user.last_name  || '',
                    email:      res.data.user.email      || '',
                    phone:      res.data.user.phone      || '',
                    country:    res.data.user.country    || '',
                    town:       res.data.user.town       || '',
                });
                if (res.data.podcaster) {
                    setPodcasterData({
                        contact_info:      res.data.podcaster.contact_info      || '',
                        subscription_type: res.data.podcaster.subscription_type || '',
                    });
                }
            })
            .catch((err) => setError(err.response?.data?.message || "Error loading profile"))
            .finally(() => setLoading(false));
    };

    const loadNotifPrefs = () => {
        httpService.get('/dash/notification-preferences')
            .then((res) => {
                const map = {};
                (res.data.data ?? []).forEach(p => {
                    map[p.notification_type] = { in_app: p.in_app, email: p.email, sms: p.sms };
                });
                setNotifPrefs(prev => ({ ...prev, ...map }));
            })
            .catch(() => {});
    };

    /* ── Handlers ── */
    const handleInputChange     = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    const handlePasswordChange  = (e) => setPasswordData(p => ({ ...p, [e.target.name]: e.target.value }));
    const handlePodcasterChange = (e) => setPodcasterData(p => ({ ...p, [e.target.name]: e.target.value }));

    const handleProfileUpdate = (e) => {
        e.preventDefault(); setSaving(true); setError(null); setSuccess(null);
        httpService.put("/dash/profile", formData)
            .then((res) => { setSuccess("Profile updated!"); setUser(res.data.user); })
            .catch((err) => setError(err.response?.data?.message || "Error updating profile"))
            .finally(() => setSaving(false));
    };

    const handlePasswordUpdate = (e) => {
        e.preventDefault(); setSaving(true); setError(null); setSuccess(null);
        httpService.put("/dash/profile/password", passwordData)
            .then(() => {
                setSuccess("Password updated!");
                setPasswordData({ current_password: '', new_password: '', new_password_confirmation: '' });
            })
            .catch((err) => setError(err.response?.data?.message || "Error updating password"))
            .finally(() => setSaving(false));
    };

    const handlePodcasterUpdate = (e) => {
        e.preventDefault(); setSaving(true); setError(null); setSuccess(null);
        httpService.put("/dash/profile/podcaster", podcasterData)
            .then((res) => { setSuccess("Podcaster profile updated!"); setPodcaster(res.data.podcaster); })
            .catch((err) => setError(err.response?.data?.message || "Error updating podcaster profile"))
            .finally(() => setSaving(false));
    };

    const handleSwitchToPodcaster = () => {
        setSwitchingRole(true);
        httpService.post('/dash/profile/switch-to-podcaster')
            .then((res) => {
                setAuthUser(res.data.user);
                navigate('/podcaster-dashboard');
            })
            .catch((err) => setError(err.response?.data?.message || 'Error switching role'))
            .finally(() => { setSwitchingRole(false); setShowSwitchModal(false); });
    };

    const handlePrefToggle = (type, channel, value) => {
        const key = `${type}.${channel}`;
        setSavingPref(prev => ({ ...prev, [key]: true }));
        setNotifPrefs(prev => ({ ...prev, [type]: { ...prev[type], [channel]: value } }));
        httpService.put(`/dash/notification-preferences/${type}`, { [channel]: value })
            .catch(() => setNotifPrefs(prev => ({ ...prev, [type]: { ...prev[type], [channel]: !value } })))
            .finally(() => setSavingPref(prev => ({ ...prev, [key]: false })));
    };

    /* ── Avatar crop ── */
    const handleAvatarUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.addEventListener('load', () => { setImageSrc(reader.result); setShowCropModal(true); });
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

    const createImage = (url) => new Promise((resolve, reject) => {
        const img = new window.Image();
        img.addEventListener('load', () => resolve(img));
        img.addEventListener('error', reject);
        img.setAttribute('crossOrigin', 'anonymous');
        img.src = url;
    });

    const rotateSize = (w, h, rot) => {
        const r = (rot * Math.PI) / 180;
        return {
            width:  Math.abs(Math.cos(r) * w) + Math.abs(Math.sin(r) * h),
            height: Math.abs(Math.sin(r) * w) + Math.abs(Math.cos(r) * h),
        };
    };

    const getCroppedImg = async (src, px, rot = 0) => {
        const image  = await createImage(src);
        const canvas = document.createElement('canvas');
        const ctx    = canvas.getContext('2d');
        const rotRad = (rot * Math.PI) / 180;
        const { width: bw, height: bh } = rotateSize(image.width, image.height, rot);
        canvas.width = bw; canvas.height = bh;
        ctx.translate(bw / 2, bh / 2);
        ctx.rotate(rotRad);
        ctx.translate(-image.width / 2, -image.height / 2);
        ctx.drawImage(image, 0, 0);
        const cc = document.createElement('canvas');
        const cctx = cc.getContext('2d');
        cc.width = px.width; cc.height = px.height;
        cctx.drawImage(canvas, px.x, px.y, px.width, px.height, 0, 0, px.width, px.height);
        return new Promise((res, rej) =>
            cc.toBlob(blob => blob ? res(blob) : rej(new Error('Canvas is empty')), 'image/jpeg', 0.95)
        );
    };

    const handleCropSave = async () => {
        if (!croppedAreaPixels) { setError('Please adjust the crop area before saving'); return; }
        try {
            setUploadingAvatar(true); setError(null); setSuccess(null);
            const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
            const fd   = new FormData();
            fd.append('avatar', blob, 'avatar.jpg');
            const res = await httpService.post("/dash/profile/avatar", fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            // httpService interceptor auto-fires the toast from res.data.toast
            // Use avatar_url + cache-bust so browser doesn't serve the stale file
            const freshUrl = res.data.avatar_url || '';
            setUser(prev => ({ ...prev, avatar_image: freshUrl + '?t=' + Date.now() }));
            setShowCropModal(false); setImageSrc(null); setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Error uploading avatar");
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleCropCancel = () => {
        setShowCropModal(false); setImageSrc(null);
        setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
    };

    /* ── Loading state ── */
    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </div>
        );
    }

    const fullName = [formData.first_name, formData.last_name].filter(Boolean).join(' ') || 'Your Profile';

    /* ══════════════════════════════════════════════
       Render
       ══════════════════════════════════════════════ */
    return (
        <div id="Profile-component">

            {/* ── Alerts ── */}
            {(error || success) && (
                <div className="container-lg px-3 px-lg-4 pt-3">
                    {error   && <Alert variant="danger"  dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
                </div>
            )}

            {/* ── Hero ── */}
            <PageHero minHeight="260px">
                <div className="pf-hero__inner">
                    {/* Avatar / initials */}
                    <UserAvatar
                        user={user}
                        size={108}
                        bordered
                        style={{ margin: '0 auto 0.9rem' }}
                    />

                    {/* Name */}
                    <div className="pf-hero__name">{fullName}</div>

                    {/* Role / location badges */}
                    <div className="d-flex gap-2 justify-content-center mb-2 flex-wrap">
                        {listener  && <Badge bg="light" text="dark" style={{ fontSize: '0.69rem' }}>Listener</Badge>}
                        {podcaster && <Badge bg="light" text="dark" style={{ fontSize: '0.69rem' }}>Podcaster</Badge>}
                        {formData.town && (
                            <Badge bg="light" text="dark" style={{ fontSize: '0.69rem', opacity: 0.8 }}>
                                {[formData.town, formData.country].filter(Boolean).join(', ')}
                            </Badge>
                        )}
                    </div>

                    {/* Avatar upload */}
                    <Form.Label htmlFor="avatar-upload" className="pf-hero__upload-btn">
                        <FaCamera /> {uploadingAvatar ? 'Uploading…' : 'Change Photo'}
                    </Form.Label>
                    <Form.Control
                        id="avatar-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,image/gif"
                        onChange={handleAvatarUpload}
                        disabled={uploadingAvatar}
                        style={{ display: 'none' }}
                    />
                </div>
            </PageHero>

            {/* ── Tab content ── */}
            <div className="container-lg py-4 px-3 px-lg-4">
                <Tabs defaultActiveKey="personal" className="mb-4">

                    {/* ══ Personal Info ══════════════════════ */}
                    <Tab
                        eventKey="personal"
                        title={<span><FaUser className="me-1" />Personal Info</span>}
                    >
                        <Form onSubmit={handleProfileUpdate}>

                            <div className="pf-form-section">
                                <div className="pf-section-label">Basic Information</div>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>First Name</Form.Label>
                                            <Form.Control
                                                type="text" name="first_name"
                                                value={formData.first_name}
                                                onChange={handleInputChange} required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Last Name</Form.Label>
                                            <Form.Control
                                                type="text" name="last_name"
                                                value={formData.last_name}
                                                onChange={handleInputChange} required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="pf-form-section">
                                <div className="pf-section-label">Contact</div>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Email</Form.Label>
                                            <Form.Control
                                                type="email" name="email"
                                                value={formData.email}
                                                onChange={handleInputChange} required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Phone</Form.Label>
                                            <Form.Control
                                                type="tel" name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="pf-form-section">
                                <div className="pf-section-label">Location</div>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Country</Form.Label>
                                            <Form.Control
                                                type="text" name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Town / City</Form.Label>
                                            <Form.Control
                                                type="text" name="town"
                                                value={formData.town}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="d-flex justify-content-end">
                                <Button type="submit" variant="primary" disabled={saving}>
                                    <FaSave className="me-1" />
                                    {saving ? 'Saving…' : 'Save Changes'}
                                </Button>
                            </div>
                        </Form>

                        <hr className="my-4" />
                        <div className="pf-form-section">
                            <div className="pf-section-label">Account Type</div>
                            <p className="text-muted small mb-3">
                                Switch your account to a podcaster. You will gain access to podcaster features.
                            </p>
                            <Button variant="outline-warning" onClick={() => setShowSwitchModal(true)}>
                                Switch to Podcaster
                            </Button>
                        </div>
                    </Tab>

                    {/* ══ Password ═══════════════════════════ */}
                    <Tab
                        eventKey="password"
                        title={<span><FaLock className="me-1" />Password</span>}
                    >
                        <Form onSubmit={handlePasswordUpdate} style={{ maxWidth: '480px' }}>

                            <div className="pf-form-section">
                                <div className="pf-section-label">Change Password</div>
                                <Form.Group className="mb-3">
                                    <Form.Label>Current Password</Form.Label>
                                    <Form.Control
                                        type="password" name="current_password"
                                        value={passwordData.current_password}
                                        onChange={handlePasswordChange} required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>New Password</Form.Label>
                                    <Form.Control
                                        type="password" name="new_password"
                                        value={passwordData.new_password}
                                        onChange={handlePasswordChange}
                                        minLength={8} required
                                    />
                                    <Form.Text className="text-muted">Minimum 8 characters</Form.Text>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Confirm New Password</Form.Label>
                                    <Form.Control
                                        type="password" name="new_password_confirmation"
                                        value={passwordData.new_password_confirmation}
                                        onChange={handlePasswordChange} required
                                    />
                                </Form.Group>
                            </div>

                            <div className="d-flex justify-content-end">
                                <Button type="submit" variant="primary" disabled={saving}>
                                    <FaLock className="me-1" />
                                    {saving ? 'Updating…' : 'Update Password'}
                                </Button>
                            </div>
                        </Form>
                    </Tab>

                    {/* ══ Podcaster (conditional) ═════════════ */}
                    {podcaster && (
                        <Tab
                            eventKey="podcaster"
                            title={<span><FaMicrophone className="me-1" />Podcaster</span>}
                        >
                            <Form onSubmit={handlePodcasterUpdate} style={{ maxWidth: '560px' }}>
                                <div className="pf-form-section">
                                    <div className="pf-section-label">Podcaster Profile</div>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Contact Information</Form.Label>
                                        <Form.Control
                                            as="textarea" rows={4} name="contact_info"
                                            value={podcasterData.contact_info}
                                            onChange={handlePodcasterChange} maxLength={500}
                                        />
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label>Subscription Type</Form.Label>
                                        <Form.Control
                                            type="text" name="subscription_type"
                                            value={podcasterData.subscription_type}
                                            onChange={handlePodcasterChange} maxLength={100}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="d-flex justify-content-end">
                                    <Button type="submit" variant="primary" disabled={saving}>
                                        <FaSave className="me-1" />
                                        {saving ? 'Saving…' : 'Save Podcaster Profile'}
                                    </Button>
                                </div>
                            </Form>
                        </Tab>
                    )}

                    {/* ══ Notifications ══════════════════════ */}
                    <Tab
                        eventKey="notifications"
                        title={<span><FaBell className="me-1" />Notifications</span>}
                    >
                        <p className="text-muted small mb-4">
                            Choose how you want to be notified for each event type.
                        </p>

                        {/* Channel legend */}
                        <div className="d-flex justify-content-end mb-2" style={{ paddingRight: '1.25rem' }}>
                            <div className="d-flex gap-4">
                                {[{ label: 'In-App', disabled: false }, { label: 'Email', disabled: false }, { label: 'SMS', disabled: true }].map(({ label, disabled }) => (
                                    <span key={label} style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, color: disabled ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.35)', minWidth: '44px', textAlign: 'center' }}>
                                        {label}
                                        {disabled && <span style={{ display: 'block', fontSize: '0.55rem', fontWeight: 400, marginTop: '1px' }}>soon</span>}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {NOTIF_TYPES.map(({ key, label }) => (
                            <div key={key} className="pf-notif-card">
                                <div className="pf-notif-card__label">{label}</div>
                                <div className="pf-notif-card__channels">
                                    {['in_app', 'email', 'sms'].map(channel => (
                                        <div key={channel} className="pf-notif-channel">
                                            <Form.Check
                                                type="switch"
                                                id={`pref-${key}-${channel}`}
                                                checked={notifPrefs[key]?.[channel] ?? false}
                                                disabled={channel === 'sms' || !!savingPref[`${key}.${channel}`]}
                                                onChange={e => handlePrefToggle(key, channel, e.target.checked)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </Tab>

                </Tabs>
            </div>

            {/* ── Switch to podcaster modal ── */}
            <Modal show={showSwitchModal} onHide={() => setShowSwitchModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Switch to Podcaster?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    You will gain access to podcaster features including the ability to create podcast clubs and episodes.
                    This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSwitchModal(false)} disabled={switchingRole}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={handleSwitchToPodcaster} disabled={switchingRole}>
                        {switchingRole ? 'Switching…' : 'Yes, switch to podcaster'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Crop modal ── */}
            <Modal dialogClassName="pf-crop-modal" show={showCropModal} onHide={handleCropCancel} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Crop Your Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ position: 'relative', width: '100%', height: '400px' }}>
                        {imageSrc && (
                            <Cropper
                                image={imageSrc}
                                crop={crop} zoom={zoom} rotation={rotation}
                                aspect={1} cropShape="round" showGrid={false}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                onRotationChange={setRotation}
                            />
                        )}
                    </div>
                    <div className="mt-3">
                        <Form.Group className="mb-2">
                            <Form.Label>Zoom</Form.Label>
                            <Form.Range min={1} max={3} step={0.1} value={zoom}
                                onChange={e => setZoom(parseFloat(e.target.value))} />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Rotation</Form.Label>
                            <Form.Range min={0} max={360} step={1} value={rotation}
                                onChange={e => setRotation(parseInt(e.target.value))} />
                        </Form.Group>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCropCancel} disabled={uploadingAvatar}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCropSave} disabled={uploadingAvatar}>
                        {uploadingAvatar ? 'Uploading…' : 'Save Photo'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default Profile;
